import { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { Spinner, EmptyState, SectionCard, TagPill } from '@/components/Common';
import { toast } from 'sonner';

/**
 * GL-SPEC-APP-037 — Engagement Audit Trail (History tab).
 *
 * Renders a reverse-chronological timeline of curated milestone events:
 * engagement status changes, finding CRUD, gap resolved, report
 * generated / sent / opened. Read-only view.
 */
const EVENT_META = {
  engagement_status: { tone: 'navy', label: 'Status' },
  finding_created: { tone: 'green', label: 'Finding · New' },
  finding_updated: { tone: 'amber', label: 'Finding · Edit' },
  finding_deleted: { tone: 'red', label: 'Finding · Delete' },
  gap_resolved: { tone: 'green', label: 'Gap · Resolved' },
  report_generated: { tone: 'navy', label: 'Report · Generated' },
  report_sent: { tone: 'navy', label: 'Report · Sent' },
  report_opened: { tone: 'green', label: 'Report · Opened' },
};

function fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function HistoryTab({ engagement }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/engagements/${engagement.id}/audit`)
      .then((r) => mounted && setEvents(r.data || []))
      .catch((e) => {
        if (mounted) {
          toast.error(formatApiError(e));
          setEvents([]);
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [engagement.id]);

  if (loading) return <div className="p-6"><Spinner label="Loading history…" /></div>;

  if (!events || events.length === 0) {
    return (
      <div className="p-4">
        <SectionCard title="Engagement History" subtitle="Nothing recorded yet. Milestone events appear here as they happen.">
          <EmptyState
            title="No activity yet"
            message="Once findings are created, gaps resolved, or the report is delivered, you'll see a timeline of activity here."
          />
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6" data-testid="engagement-history-tab">
      <SectionCard
        title="Engagement History"
        subtitle={`${events.length} event${events.length === 1 ? '' : 's'} — most recent first`}
      >
        <ol className="relative border-l-2 ml-2" style={{ borderColor: 'var(--gl-border-strong, #e5e7eb)' }}>
          {events.map((evt) => {
            const meta = EVENT_META[evt.event_type] || { tone: 'neutral', label: evt.event_type };
            return (
              <li key={evt.id} className="pl-5 pb-5 relative" data-testid={`audit-event-${evt.event_type}`}>
                <span
                  aria-hidden="true"
                  className="absolute -left-[7px] top-1 w-3 h-3 rounded-full"
                  style={{ background: 'var(--gl-navy, #142654)', border: '2px solid #ffffff' }}
                />
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <TagPill tone={meta.tone}>{meta.label}</TagPill>
                  <span
                    className="text-[11px] uppercase tracking-wider"
                    style={{ color: 'var(--gl-muted)', fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {fmtWhen(evt.created_at)}
                  </span>
                  {evt.actor_type === 'service' && <TagPill tone="amber">service token</TagPill>}
                </div>
                <div className="text-sm" style={{ color: 'var(--gl-ink, #111827)' }}>{evt.summary}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--gl-muted)' }}>
                  by {evt.actor_email || 'system'}
                </div>
              </li>
            );
          })}
        </ol>
      </SectionCard>
    </div>
  );
}
